import { CartAddEvent } from '@theme/events';

class CustomCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    document.querySelectorAll('[data-custom-cart-trigger]').forEach((button) => button.addEventListener('click', this.open, { signal }));
    document.addEventListener(CartAddEvent.eventName, this.onCartAdd, { signal });
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('change', this.onChange, { signal });
    this.addEventListener('submit', this.onSubmit, { signal });
    this.dialog?.addEventListener('close', () => this.classList.remove('is-offers-open'), { signal });
    this.trackCompletedTiers();
    this.loadUpsells();
  }

  disconnectedCallback() { this.abortController?.abort(); }
  track(name, parameters = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...parameters });
    if (typeof window.clarity === 'function') window.clarity('event', name);
  }
  open = () => { this.dialog?.showModal(); this.track('cart_drawer_open'); };
  onCartAdd = async (event) => {
    if (event.detail?.data?.didError) return;
    await this.refresh({ open: this.dataset.autoOpen === 'true' });
  };
  closeOffers() { this.classList.remove('is-offers-open'); this.querySelector('[data-offers-sheet]')?.setAttribute('aria-hidden', 'true'); }
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-cart-close]')) this.dialog?.close();
    if (target?.closest('[data-offers-open]')) { this.classList.add('is-offers-open'); this.querySelector('[data-offers-sheet]')?.setAttribute('aria-hidden', 'false'); }
    if (target?.closest('[data-offers-close]')) this.closeOffers();
    const offer = target?.closest('[data-apply-offer]')?.closest('[data-offer]');
    if (offer instanceof HTMLElement) this.applyCoupon(offer.dataset.code || '');
    const removeCoupon = target?.closest('[data-remove-coupon]');
    if (removeCoupon instanceof HTMLElement) this.removeCoupon(removeCoupon.dataset.removeCoupon || '');
    if (target?.closest('[data-upsell-prev]')) this.scrollUpsells(-1);
    if (target?.closest('[data-upsell-next]')) this.scrollUpsells(1);
    const variant = target?.closest('[data-upsell-variant-button]');
    if (variant instanceof HTMLButtonElement) this.selectUpsellVariant(variant);
    const add = target?.closest('[data-upsell-add]');
    if (add instanceof HTMLButtonElement) this.addUpsell(add);
    if (target?.closest('[data-checkout]')) {
      this.track('begin_checkout');
      if (typeof window.fbq === 'function') window.fbq('track', 'InitiateCheckout');
    }
  };
  selectUpsellVariant(button) {
    const card = button.closest('[data-upsell-card]');
    const select = card?.querySelector('[data-upsell-variant]');
    if (!(select instanceof HTMLSelectElement)) return;
    select.value = button.dataset.variantId || '';
    select.dispatchEvent(new Event('change', { bubbles: true }));
    card.querySelectorAll('[data-upsell-variant-button]').forEach((option) => {
      const selected = option === button;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-checked', String(selected));
    });
  }
  onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-upsell-variant]')) return;
    const price = select.closest('[data-upsell-card]')?.querySelector('[data-upsell-price]');
    const selectedVariant = select.selectedOptions[0];
    if (price && selectedVariant?.dataset.price) price.textContent = this.formatMoney(Number(selectedVariant.dataset.price));
  };
  onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-coupon-form]')) return;
    event.preventDefault();
    const code = new FormData(form).get('discount');
    if (typeof code === 'string') this.applyCoupon(code);
  };
  appliedCouponCodes() {
    return Array.from(this.querySelectorAll('[data-applied-coupon]'))
      .map((coupon) => coupon instanceof HTMLElement ? coupon.dataset.appliedCoupon : '')
      .filter(Boolean);
  }
  setCouponStatus(message, isError = false) {
    const status = this.querySelector('[data-coupon-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', isError);
  }
  async applyCoupon(code) {
    const normalizedCode = code.trim();
    if (!normalizedCode) return;
    const existingCodes = this.appliedCouponCodes();
    if (existingCodes.some((existingCode) => existingCode.toLowerCase() === normalizedCode.toLowerCase())) {
      this.setCouponStatus(`${normalizedCode} is already applied.`);
      return;
    }
    this.track('coupon_attempt', { coupon: normalizedCode });
    this.setCouponStatus('Validating offer…');
    try {
      const response = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: [...existingCodes, normalizedCode].join(','),
          sections: [this.dataset.sectionId],
        }),
      });
      if (!response.ok) throw new Error('Coupon could not be applied');
      const cart = await response.json();
      const discount = cart.discount_codes?.find(({ code: returnedCode }) =>
        typeof returnedCode === 'string' && returnedCode.toLowerCase() === normalizedCode.toLowerCase()
      );
      if (!discount?.applicable) throw new Error('Enter a valid discount code');
      this.track('coupon_applied', { coupon: normalizedCode });
      this.closeOffers();
      await this.refresh();
    } catch (error) {
      this.setCouponStatus(error instanceof Error ? error.message : 'Coupon could not be applied', true);
    }
  }
  async removeCoupon(code) {
    const remainingCodes = this.appliedCouponCodes().filter(
      (appliedCode) => appliedCode.toLowerCase() !== code.toLowerCase()
    );
    this.setCouponStatus('Removing offer…');
    try {
      const response = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: remainingCodes.join(','), sections: [this.dataset.sectionId] }),
      });
      if (!response.ok) throw new Error('Coupon could not be removed');
      this.track('coupon_removed', { coupon: code });
      await this.refresh();
    } catch (error) {
      this.setCouponStatus(error instanceof Error ? error.message : 'Coupon could not be removed', true);
    }
  }
  trackCompletedTiers() {
    this.querySelectorAll('[data-progress] .is-complete').forEach((tier, index) => {
      const key = `custom-cart-tier-${index + 1}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, 'true');
      this.track('cart_tier_complete', { tier: index + 1 });
    });
  }
  async refresh({ open = false } = {}) {
    const response = await fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}`);
    if (!response.ok) return;
    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const next = documentFragment.querySelector('custom-cart-drawer');
    if (!next) return;
    const wasOpen = this.dialog?.open;
    this.replaceWith(next);
    if (wasOpen || open) next.querySelector('dialog')?.showModal();
    return next;
  }
  async addUpsell(button) {
    const card = button.closest('[data-upsell-card]');
    const select = card?.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) return;
    button.disabled = true;
    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(select.value), quantity: 1 }] }) });
      if (!response.ok) throw new Error();
      this.track('upsell_add', { item_id: select.value });
      await this.refresh();
    } finally { button.disabled = false; }
  }
  scrollUpsells(direction) { this.querySelector('[data-upsell-list]')?.scrollBy({ left: direction * 180, behavior: 'smooth' }); }
  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'INR';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency }).format(cents / 100);
  }
  async getRecommendations(productId, intent) {
    const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${this.dataset.upsellLimit || 6}&intent=${intent}`);
    if (!response.ok) return [];
    return (await response.json()).products || [];
  }
  async loadUpsells() {
    const list = this.querySelector('[data-upsell-list]');
    if (!list) return;
    try {
      const cart = await fetch('/cart.js').then((response) => response.json());
      const cartProductIds = new Set(cart.items.map((item) => item.product_id));
      const gathered = [];
      if (!cart.items.length && this.dataset.emptyProduct) gathered.push(...await this.getRecommendations(this.dataset.emptyProduct, 'related'));
      for (const item of cart.items) gathered.push(...await this.getRecommendations(item.product_id, 'complementary'));
      for (const item of cart.items) gathered.push(...await this.getRecommendations(item.product_id, 'related'));
      const unique = [...new Map(gathered.map((product) => [product.id, product])).values()]
        .filter((product) => product.available && !cartProductIds.has(product.id)).slice(0, Number(this.dataset.upsellLimit || 6));
      if (!unique.length) { list.closest('[data-upsells]')?.remove(); return; }
      list.innerHTML = unique.map((product) => this.renderProduct(product)).join('');
    } catch { list.closest('[data-upsells]')?.remove(); }
  }
  renderProduct(product) {
    const variants = product.variants.filter((variant) => variant.available);
    if (!variants.length) return '';
    const options = variants.map((variant) => `<option value="${variant.id}" data-price="${variant.price}">${this.escape(variant.title)}</option>`).join('');
    const variantButtons = variants.map((variant, index) => `<button type="button" class="custom-cart__upsell-variant${index === 0 ? ' is-selected' : ''}" role="radio" aria-checked="${index === 0}" data-upsell-variant-button data-variant-id="${variant.id}">${this.escape(variant.title)}</button>`).join('');
    return `<article class="custom-cart__upsell-card" data-upsell-card>
      <a class="custom-cart__upsell-image" href="${product.url}"><img src="${product.featured_image || ''}" alt="${this.escape(product.title)}" loading="lazy"></a>
      <div class="custom-cart__upsell-content"><h4>${this.escape(product.title)}</h4><span class="price" data-upsell-price aria-live="polite">${this.formatMoney(variants[0].price)}</span></div>
      <div class="custom-cart__upsell-actions">
        <div class="custom-cart__upsell-variants${variants.length > 2 ? ' has-many' : ''}" role="radiogroup" aria-label="Choose ${this.escape(product.title)} variant">${variantButtons}</div>
        <select class="visually-hidden" data-upsell-variant tabindex="-1" aria-hidden="true">${options}</select>
        <button class="custom-cart__upsell-add" type="button" data-upsell-add>Add to Bag</button>
      </div>
    </article>`;
  }
  escape(value = '') { const element = document.createElement('div'); element.textContent = String(value); return element.innerHTML; }
}

if (!customElements.get('custom-cart-drawer')) customElements.define('custom-cart-drawer', CustomCartDrawer);
